export const LEGAL_OPERATOR = {
  legalName: "SmartManager LLC",
  serviceName: "Smart Manager",
  noticeAddress: ["480 Boynton Ave", "San Jose, California 95117", "United States"],
  legalEmail: "jonathan@smartmanager.ai",
  supportEmail: "jonathan@smartmanager.ai",
} as const;

export const CURRENT_LEGAL_DOCUMENTS = {
  terms: {
    version: "2026-10-17",
    sha256: "6bcd4c3f3f71615d80490c5eb3c1595e8c2fa28aceb1d184a88e53ecff115814",
    href: "/terms",
    label: "Terms of Service",
  },
  privacy: {
    version: "2026-10-23",
    sha256: "0d8e93faced3529b027fb6cfe99908466d15197e5e56b66b5f0d67f44941a793",
    href: "/privacy-policy",
    noticeHref: "/privacy-policy#s-notice-at-collection",
    label: "Privacy Policy",
  },
  billingDisclosureVersion: "2026-10-22",
} as const;

/**
 * The newer of the Terms and Privacy Policy versions. Versions are ISO dates
 * (YYYY-MM-DD), so the later one sorts last.
 */
export const LATEST_LEGAL_VERSION: string = [
  CURRENT_LEGAL_DOCUMENTS.terms.version,
  CURRENT_LEGAL_DOCUMENTS.privacy.version,
].sort().at(-1) ?? CURRENT_LEGAL_DOCUMENTS.terms.version;

/** Plain words for people ("September 23, 2026"), not the ISO version string. */
export function formatLegalVersionDate(version: string): string {
  return new Date(`${version}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * The identity of the documents a user must accept now. The server is its
 * only source: clients (the native app especially) read it from the session
 * bootstrap, the consent response and the 403 legal_acceptance_required body
 * instead of keeping their own copy that drifts when the documents change.
 */
export type LegalDocumentIdentity = {
  terms: { version: string; sha256: string };
  privacy: { version: string; sha256: string };
};

export function currentLegalDocumentIdentity(): LegalDocumentIdentity {
  return {
    terms: { version: CURRENT_LEGAL_DOCUMENTS.terms.version, sha256: CURRENT_LEGAL_DOCUMENTS.terms.sha256 },
    privacy: { version: CURRENT_LEGAL_DOCUMENTS.privacy.version, sha256: CURRENT_LEGAL_DOCUMENTS.privacy.sha256 },
  };
}

export function getLegalDocumentHref(href: string, electronRuntime: boolean): string {
  if (!electronRuntime) return href;
  return new URL(href, "https://www.smartmanager.ai").toString();
}

export type LegalAcceptanceSource = "service_access" | "subscription_checkout";

export type LegalAcceptanceSummary = {
  termsVersion: string;
  privacyVersion: string;
  acceptedAt: number;
  source: LegalAcceptanceSource;
  receiptId: string;
  termsSha256: string;
  privacySha256: string;
  /** When the user attested they meet MINIMUM_USER_AGE; carried forward by later acceptances. */
  ageAttestedAt?: number;
};

export type LegalConsentPayload = {
  accepted: true;
  termsVersion: string;
  privacyVersion: string;
  /** Required by the service-access gate; see validateServiceAccessConsentPayload. */
  ageAttested?: true;
};

/** The service is for adults. Attested at the consent gate, not verified. */
export const MINIMUM_USER_AGE = 18;

export type SubscriptionConsentPayload = LegalConsentPayload & {
  billingDisclosureVersion: string;
  recurringBillingAuthorized: true;
};

export function hasAcceptedCurrentLegalDocuments(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const acceptance = value as Partial<LegalAcceptanceSummary>;
  return (
    acceptance.termsVersion === CURRENT_LEGAL_DOCUMENTS.terms.version
    && acceptance.privacyVersion === CURRENT_LEGAL_DOCUMENTS.privacy.version
    && acceptance.termsSha256 === CURRENT_LEGAL_DOCUMENTS.terms.sha256
    && acceptance.privacySha256 === CURRENT_LEGAL_DOCUMENTS.privacy.sha256
    && typeof acceptance.acceptedAt === "number"
    && Number.isFinite(acceptance.acceptedAt)
    && acceptance.acceptedAt > 0
    && acceptance.acceptedAt <= Date.now() + 5 * 60 * 1000
    && (acceptance.source === "service_access" || acceptance.source === "subscription_checkout")
    && typeof acceptance.receiptId === "string"
    && acceptance.receiptId.length > 0
    && acceptance.receiptId.length <= 128
    // Checkout records acceptance without the 18+ attestation; only the
    // service-access gate collects it, so an acceptance without it is not
    // complete and the gate asks again.
    && typeof acceptance.ageAttestedAt === "number"
    && Number.isSafeInteger(acceptance.ageAttestedAt)
    && acceptance.ageAttestedAt > 0
  );
}

export function validateLegalConsentPayload(value: unknown): value is LegalConsentPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as Partial<LegalConsentPayload>;
  return (
    payload.accepted === true
    && payload.termsVersion === CURRENT_LEGAL_DOCUMENTS.terms.version
    && payload.privacyVersion === CURRENT_LEGAL_DOCUMENTS.privacy.version
  );
}

export function validateSubscriptionConsentPayload(
  value: unknown,
): value is SubscriptionConsentPayload {
  if (!validateLegalConsentPayload(value)) return false;
  const payload = value as Partial<SubscriptionConsentPayload>;
  return (
    payload.recurringBillingAuthorized === true
    && payload.billingDisclosureVersion === CURRENT_LEGAL_DOCUMENTS.billingDisclosureVersion
  );
}

/** The first-run gate: current documents plus the 18+ attestation. */
export function validateServiceAccessConsentPayload(
  value: unknown,
): value is LegalConsentPayload & { ageAttested: true } {
  return validateLegalConsentPayload(value) && (value as Partial<LegalConsentPayload>).ageAttested === true;
}
